import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { GlassCard } from '../ui/GlassCard';
import { ArrowRight, Activity, Eye, Users, Target, ShieldAlert } from 'lucide-react';
type Insight = {
  observed: string;
  metric: string;
  type: string;
  typeColor: string;
  insight: string;
  icon: ComponentType<{
    className?: string;
  }>;
};
const INSIGHTS: Insight[] = [{
  observed: 'Revisits to the safety section',
  metric: '67%',
  type: 'Motivation',
  typeColor: '#0085bd',
  insight: 'Risk concern is the dominant decision driver — not efficacy.',
  icon: ShieldAlert
}, {
  observed: 'Cardiologists vs other HCPs on guidelines',
  metric: '3.2×',
  type: 'Curiosity',
  typeColor: '#7c5cf5',
  insight: 'Deep scientific validation matters most for this specialty.',
  icon: Eye
}, {
  observed: 'APAC users skipping long-form video',
  metric: '−54%',
  type: 'Segment',
  typeColor: '#0085bd',
  insight: 'Quick summaries outperform — regional content needs reformatting.',
  icon: Users
}, {
  observed: 'Hesitation at CTA, then exit',
  metric: '32%',
  type: 'Hesitation',
  typeColor: '#7c5cf5',
  insight: 'Trust gap right before commitment — add proof or testimonial here.',
  icon: Target
}];
export function Slide07Behavioral() {
  return <div className="w-full h-full flex flex-col max-w-[88rem] mx-auto pt-6" data-id="element-169">
      <motion.h2 className="text-[clamp(2rem,3.4vw,3.4rem)] font-light tracking-tight mb-2 text-center text-snakker-ink" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-170">
        From <span className="text-snakker-subtle" data-id="element-171">what they did</span>{' '}
        &nbsp;→&nbsp; to{' '}
        <span className="text-gradient-accent font-medium" data-id="element-172">
          why they did it.
        </span>
      </motion.h2>
      <motion.p className="text-center text-snakker-muted text-sm mb-8" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.2
    }} data-id="element-173">
        Every behavioral signal Snakker captures answers a strategic question.
      </motion.p>

      {/* Column headers */}
      <div className="grid grid-cols-[1fr_auto_1.3fr] gap-6 px-4 mb-3" data-id="element-174">
        <div className="text-[10px] font-mono uppercase tracking-widest text-snakker-muted flex items-center gap-2" data-id="element-175">
          <Activity className="w-3 h-3" data-id="element-176" /> Observed behavior
        </div>
        <div className="w-12" data-id="element-177" />
        <div className="text-[10px] font-mono uppercase tracking-widest text-snakker-secondary" data-id="element-178">
          Strategic insight
        </div>
      </div>

      {/* Behavior → Insight rows */}
      <div className="flex-1 flex flex-col gap-3" data-id="element-179">
        {INSIGHTS.map((row, i) => <motion.div key={i} className="grid grid-cols-[1fr_auto_1.3fr] gap-6 items-stretch" initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4 + i * 0.18
      }} data-id="element-180">
            {/* LEFT: Observed behavior */}
            <div className="rounded-xl border border-snakker-border bg-white p-4 flex items-center gap-4" data-id="element-181">
              <div className="w-10 h-10 rounded-lg bg-snakker-ink/[0.04] flex items-center justify-center flex-shrink-0" data-id="element-182">
                <row.icon className="w-5 h-5 text-snakker-muted" data-id="element-183" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-184">
                <div className="text-sm text-snakker-muted truncate" data-id="element-185">
                  {row.observed}
                </div>
                <div className="font-display text-2xl font-medium text-snakker-ink leading-tight" data-id="element-186">
                  {row.metric}
                </div>
              </div>
            </div>

            {/* CENTER: arrow + type tag */}
            <div className="flex flex-col items-center justify-center gap-2 w-32" data-id="element-187">
              <motion.div className="flex items-center gap-1" style={{
            color: row.typeColor
          }} animate={{
            x: [0, 4, 0]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            delay: i * 0.3
          }} data-id="element-188">
                <div className="h-px w-8" style={{
              background: row.typeColor
            }} data-id="element-189" />
                <ArrowRight className="w-4 h-4" data-id="element-190" />
              </motion.div>
              <div className="px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest" style={{
            backgroundColor: `${row.typeColor}15`,
            color: row.typeColor,
            border: `1px solid ${row.typeColor}40`
          }} data-id="element-191">
                {row.type}
              </div>
            </div>

            {/* RIGHT: Strategic insight */}
            <div className="rounded-xl p-4 flex items-center relative overflow-hidden" style={{
          backgroundColor: `${row.typeColor}08`,
          border: `1px solid ${row.typeColor}33`
        }} data-id="element-192">
              <div className="absolute top-0 left-0 w-1 h-full" style={{
            background: row.typeColor
          }} data-id="element-193" />
              <p className="text-snakker-ink leading-snug pl-3" data-id="element-194">
                {row.insight}
              </p>
            </div>
          </motion.div>)}
      </div>

      <motion.div className="text-center mt-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.4
    }} data-id="element-195">
        <p className="text-base font-light tracking-wide text-snakker-ink" data-id="element-196">
          Snakker reveals{' '}
          <span className="font-medium" data-id="element-197">motivation, hesitation, curiosity</span>{' '}
          — the <span className="text-snakker-secondary font-medium" data-id="element-198">why</span>{' '}
          behind every signal.
        </p>
      </motion.div>
    </div>;
}